import { AiProvider } from './types'

/** Regions where Bedrock Mantle is supported by both runtime configuration and deployment IAM. */
export const AI_MANTLE_SUPPORTED_REGIONS = ['eu-north-1', 'eu-west-1', 'eu-west-2', 'us-east-1'] as const

export type AiMantleSupportedRegion = (typeof AI_MANTLE_SUPPORTED_REGIONS)[number]

/** Checks a runtime setting against the regions granted by deployment IAM. */
export const isAiMantleSupportedRegion = (region: string): region is AiMantleSupportedRegion =>
    (AI_MANTLE_SUPPORTED_REGIONS as readonly string[]).includes(region)

/** Operator-facing provider names, used in Billing Manager and in provider error messages. */
export const AI_PROVIDER_LABELS: Record<AiProvider, string> = {
    [AiProvider.bedrockMantle]: 'Bedrock Mantle',
    [AiProvider.anthropic]: 'Anthropic',
}

/**
 * Canonical model names are Bedrock-style (`anthropic.claude-opus-5`). The direct Anthropic
 * Messages API needs its own ids, so the mapping is explicit rather than a prefix rewrite.
 */
const ANTHROPIC_MODEL_IDS: Record<string, string> = {
    'anthropic.claude-opus-5-5': 'claude-opus-5-5',
    'anthropic.claude-opus-5': 'claude-opus-5',
    'anthropic.claude-opus-4-8': 'claude-opus-4-8',
    'anthropic.claude-opus-4-7': 'claude-opus-4-7',
    'anthropic.claude-opus-4-6': 'claude-opus-4-6',
    'anthropic.claude-sonnet-5': 'claude-sonnet-5',
    'anthropic.claude-sonnet-4-6': 'claude-sonnet-4-6',
    'anthropic.claude-haiku-4-5': 'claude-haiku-4-5-20251001',
}

/** @returns The direct Anthropic model id, or undefined when the model is not served by Anthropic. */
export const anthropicModelId = (model: string): string | undefined => ANTHROPIC_MODEL_IDS[model]

/** @returns The canonical model name for a direct Anthropic model id, or undefined when unmapped. */
export const canonicalAnthropicModel = (anthropicId: string): string | undefined =>
    Object.entries(ANTHROPIC_MODEL_IDS).find(([, id]) => id === anthropicId)?.[0]
